import { Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/lib/theme-provider";
import { Toaster } from "@/components/ui/sonner";
import { MainLayout } from "@/components/layout/main-layout";
import { SilaeVariablesLoader } from "@/components/SilaeVariablesLoader";
import LoginPage from "./pages/auth/login";
import RegisterPage from "./pages/auth/register";
import ForgotPasswordPage from "./pages/auth/forgot-password";
import ResetPasswordPage from "./pages/auth/reset-password";
import PlansPage from "./pages/plans";
import {
  SubscriptionSuccessPage,
  SubscriptionCancelPage,
  CreditsSuccessPage,
} from "./pages/subscription";
import { AdminLogin } from "./pages/admin/AdminLogin";
import { ProtectedAdminDashboard } from "./pages/admin/ProtectedAdminDashboard";
import { DashboardLayout } from "./pages/dashboard/DashboardLayout";

import { ProfilePage } from "./pages/dashboard/ProfilePage";
import { SubscriptionPage } from "./pages/dashboard/SubscriptionPage";
import { WorkspaceTab } from "./pages/dashboard/tabs/WorkspaceTab";
import { VariablesTab } from "./pages/dashboard/tabs/VariablesTab";
import { CodeEditorTab } from "./pages/dashboard/tabs/CodeEditorTab";
import { AiGeneratorTab } from "./pages/dashboard/tabs/AiGeneratorTab";
import PublicHomePage from "./pages/PublicHomePage";
import { FeatureGuard } from "./components/auth/FeatureGuard";

// Guards de route basés sur Redux
import { UserProtectedRoute } from "./components/auth/UserProtectedRoute";
import { PublicRoute } from "./components/auth/PublicRoute";

/**
 * App Component
 *
 * Architecture d'authentification basée sur Redux :
 *
 * - authSlice : Gère l'état d'authentification des utilisateurs
 * - adminSlice : Gère l'état d'authentification des administrateurs
 *
 * Les deux systèmes sont complètement isolés :
 * - Cookies séparés côté backend (token vs admin_token)
 * - Slices Redux séparés
 * - Guards de route séparés (UserProtectedRoute vs AdminProtectedRoute)
 *
 * Routes publiques d'auth (login, register, etc.) :
 * - Redirigent vers /dashboard si l'utilisateur est déjà connecté
 *
 * Note: Le loader initial est géré par PersistGate dans main.tsx
 * qui attend la réhydratation du store Redux avant de rendre l'app.
 */

function App() {
  return (
    <ThemeProvider defaultTheme="system" storageKey="app-theme">
      {/* Chargeur de variables Silae */}
      <SilaeVariablesLoader />

      <div className="min-h-screen flex-col bg-background items-center mx-auto">
        <Routes>
          {/* ROUTES PUBLIQUES (pas d'authentification)   */}
          <Route
            path="/"
            element={
              <PublicRoute>
                <MainLayout>
                  <PublicHomePage />
                </MainLayout>
              </PublicRoute>
            }
          />

          {/* ROUTES D'AUTH (redirige vers dashboard si connecté) */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            }
          />

          {/* ROUTES ADMIN (authentification admin isolée) */}
          {/* Page de login admin - pas de protection */}
          <Route path="/admin" element={<AdminLogin />} />

          {/* Dashboard admin - protégé par AdminProtectedRoute */}
          <Route
            path="/admin/dashboard"
            element={<ProtectedAdminDashboard />}
          />

          {/* ROUTES UTILISATEUR PROTÉGÉES                */}

          {/* Route de succès après paiement */}
          <Route
            path="/success"
            element={
              <UserProtectedRoute>
                <MainLayout>
                  <SubscriptionSuccessPage />
                </MainLayout>
              </UserProtectedRoute>
            }
          />

          {/* Route de succès après achat de crédits IA */}
          <Route
            path="/credits-success"
            element={
              <UserProtectedRoute>
                <MainLayout>
                  <CreditsSuccessPage />
                </MainLayout>
              </UserProtectedRoute>
            }
          />

          {/* Route d'annulation de paiement */}
          <Route
            path="/cancel"
            element={
              <UserProtectedRoute>
                <MainLayout>
                  <SubscriptionCancelPage />
                </MainLayout>
              </UserProtectedRoute>
            }
          />

          {/* Page des plans (authentifié mais pas besoin d'abonnement) */}
          <Route
            path="/plans"
            element={
              <UserProtectedRoute>
                <MainLayout>
                  <PlansPage />
                </MainLayout>
              </UserProtectedRoute>
            }
          />

          {/* DASHBOARD UTILISATEUR (nécessite abonnement) */}
          <Route
            path="/dashboard"
            element={
              <UserProtectedRoute requireSubscription>
                <DashboardLayout />
              </UserProtectedRoute>
            }
          >
            <Route
              index
              element={<Navigate to="/dashboard/code-generator" replace />}
            />

            <Route path="profile" element={<ProfilePage />} />
            <Route path="subscription" element={<SubscriptionPage />} />
            <Route
              path="code-generator"
              element={
                <FeatureGuard requiredFeature="code_generation">
                  <WorkspaceTab />
                </FeatureGuard>
              }
            />
            <Route
              path="variable_library"
              element={
                <FeatureGuard requiredFeature="variable_library">
                  <VariablesTab />
                </FeatureGuard>
              }
            />
            <Route
              path="code-editor"
              element={
                <FeatureGuard requiredFeature="code_editor">
                  <CodeEditorTab />
                </FeatureGuard>
              }
            />
            <Route
              path="ai-generator"
              element={
                <FeatureGuard requiredFeature="ai_silae">
                  <AiGeneratorTab />
                </FeatureGuard>
              }
            />
          </Route>

          {/* ROUTE FALLBACK */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>

        <Toaster />
      </div>
    </ThemeProvider>
  );
}

export default App;
